import {persistReducer, createTransform} from 'redux-persist'
import {Reducer} from 'redux'
import storage from 'redux-persist/lib/storage'
import {
  addDays,
  isBeforeDay,
  formatISODateTime,
  toPropertyDateTime,
  toLocalDateTime
} from '../services/dateUtils'
import {SearchParams, ShoppingCart} from '../schema'
import {getLogger} from '../services/logging'
import {validateShoppingCartArrivalDates} from '../services/cartValidation'

const logger = getLogger('persist')

const validateSearchTransform = createTransform(
  (search: SearchParams) => search,
  (search: SearchParams) => {
    const today = new Date()
    const checkInDate = search?.dateRange?.start

    logger.info({message: 'Validating search params...', data: {search}})

    // if check-in date is before today, reset checkin/checkout dates
    if (
      checkInDate &&
      isBeforeDay(toPropertyDateTime(checkInDate), toPropertyDateTime(today))
    ) {
      search.dateRange = {
        ...search.dateRange,
        start: formatISODateTime(toLocalDateTime(today)),
        end: formatISODateTime(addDays(today, 1))
      }
    }

    logger.info({message: 'Validating search params complete.', data: {search}})

    return search
  },
  {whitelist: ['search']}
)

const validateCartTransform = createTransform(
  (shoppingCart: ShoppingCart) => shoppingCart,
  (shoppingCart: ShoppingCart) => {
    const cartItems = shoppingCart.items

    logger.info({message: 'Validating shopping cart...', data: {shoppingCart}})

    // remove items from shopping cart if arrival date is before today
    if (cartItems) {
      shoppingCart.items = validateShoppingCartArrivalDates(cartItems)
    }

    logger.info({
      message: 'Validating shopping cart complete.',
      data: {shoppingCart}
    })

    return shoppingCart
  },
  {whitelist: ['shoppingCart']}
)

// configure redux persist - persists state to local storage
const persistConfig = {
  key: 'root',
  storage,
  whitelist: ['search', 'shoppingCart', 'session'],
  transforms: [validateCartTransform, validateSearchTransform]
}

export const createPersistReducer = (rootReducer: Reducer) => {
  return persistReducer(persistConfig, rootReducer)
}
